// Copyright 2025 - Hewlett Packard Enterprise Company
import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useLayoutEffect,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Form,
  FormField,
  Text,
  Layer,
  TextArea,
  Button,
  DataTable,
  ThumbsRating,
} from 'grommet';
import PropTypes from 'prop-types';
import { GenAIFill } from 'grommet-icons';
import { sendChatFeedback } from '../../actions/Api';
import TextEmphasis from '../TextEmphasis/TextEmphasis';
import MarkdownComponent from '../MarkdownComponent/MarkdownComponent';
import MessageLoader from './MessageLoader';
import { ChatContext } from '../../providers/ChatProvider';
import ChatQuestionButton from './ChatQuestionButton';
import DeviceDetailsLink from '../DeviceDetailsLink/DeviceDetailsLink';

const ChatAnswer = ({ query, answer, showFollowUpQuestions, showFeedback }) => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const {
    handleQueryClick,
    hideAllFollowUpQuestions,
    setIsFeedbackOpen,
    hideAllFeedback,
  } = useContext(ChatContext);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [feedbackId, setFeedbackId] = useState(null); // Store feedback_id
  const [isLiked, setIsLiked] = useState(null);
  const [feedbackLocked, setFeedbackLocked] = useState(false);
  const [chatResponse, setChatResponse] = useState({ summary: '', items: [] });
  const [leftMargin, setLeftMargin] = useState(0);
  const [showAdditionalFeedback, setShowAdditionalFeedback] = useState(false);
  const answerBoxRef = useRef(null);
  const feedbackPopupRef = useRef(null);

  useEffect(() => {
    if (showFeedbackPopup && feedbackPopupRef.current) {
      feedbackPopupRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, [showFeedbackPopup]);

  useEffect(() => {
    if (answer === 'Error retrieving answer' || answer === 'TEST') {
      setChatResponse({
        summary: t('chat.chat_error'),
        data: [],
      });
    } else if (answer !== 'Loading') {
      setChatResponse(answer);
    }
  }, [answer, t]);

  const handleLinkError = error => {
    if (error?.response?.status === 403) {
      history.replace(`/error/${error.response.status}`);
    }
  };

  const handleFeedbackClick = async feedbackType => {
    setIsLiked(feedbackType === 1);
    try {
      const response = await sendChatFeedback(
        query,
        feedbackType,
        '',
        answer.id,
      ); // Send feedback
      if (response?.data?.feedback_id) {
        setFeedbackLocked(true);
        setFeedbackId(response.data.feedback_id);
      }
      if (feedbackType === -1) {
        setShowSuccessModal(true);
        setShowFeedbackPopup(true);
        setIsFeedbackOpen(true);
      } else if (feedbackType === 1) {
        setShowFeedbackPopup(false);
        setShowSuccessModal(true);
        setIsFeedbackOpen(false);
      }
    } catch (error) {
      setShowErrorModal(true);
    }
  };

  const closePopup = () => {
    setShowSuccessModal(true);
    setShowFeedbackPopup(false);
    setIsFeedbackOpen(false);
    setFeedback('');
  };

  const submitFeedback = async () => {
    try {
      if (feedbackId) {
        await sendChatFeedback(query, isLiked ? 1 : -1, feedback, feedbackId);
      } else {
        const response = await sendChatFeedback(
          query,
          isLiked ? 1 : -1,
          feedback,
          answer.id,
        );
        if (response?.data?.feedback_id) {
          setFeedbackId(response.data.feedback_id);
        }
      }
      setShowAdditionalFeedback(true);
      setShowSuccessModal(true);
      setFeedbackLocked(true);
    } catch (error) {
      setShowErrorModal(true);
    }
    setIsFeedbackOpen(false);
    closePopup();
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
  };

  const getFeedbackMessage = () => {
    if (showAdditionalFeedback) {
      return t('chat.feedback.additional_feedback_thank_you');
    }
    if (showSuccessModal) {
      return t('chat.feedback.thank_you_message');
    }
    return t('chat.chat_feedback_text');
  };

  const handleRowData = item => {
    if (typeof item === 'object' && item !== null && item?.id && item?.name) {
      return (
        <DeviceDetailsLink
          key={item.id}
          deviceId={item.id}
          name={item.name}
          handleError={handleLinkError}
        />
      );
    }
    if (typeof item === 'boolean') {
      return item ? (
        <Text truncate="tip">{t('chat.chat_boolean_true')}</Text>
      ) : (
        <Text truncate="tip">{t('chat.chat_boolean_false')}</Text>
      );
    }
    if (typeof item === 'string' || typeof item === 'number') {
      return <Text truncate="tip">{item}</Text>;
    }
    // Fallback for unmatched conditions
    return <Text truncate="tip">--</Text>;
  };

  useLayoutEffect(() => {
    if (answerBoxRef.current) {
      setLeftMargin(answerBoxRef.current.offsetWidth);
    }
  }, [chatResponse]);

  return (
    answer && (
      <Box
        width="100%"
        direction="column"
        align="start"
        data-testid="chat-answer-outer-box"
        flex="grow"
      >
        <Box
          width="100%"
          direction="row"
          alignSelf="start"
          align="start"
          gap="small"
          pad={{ right: 'small', bottom: '0', left: '0' }}
          data-testid="chat-answer-inner-box"
          style={{ position: 'relative' }}
          flex="grow"
        >
          <Box direction="row" align="start" gap="small">
            {answer === 'Loading' ? (
              <Box direction="row" gap="small" margin={{ top: 'small' }}>
                <GenAIFill color="brand" />
                <MessageLoader />
              </Box>
            ) : (
              <>
                <Box margin={{ top: 'small' }}>
                  <GenAIFill color="brand" />
                </Box>
                <Box
                  pad={{ horizontal: 'small', vertical: 'small' }}
                  data-testid="chat-answer-summary-box"
                  flex
                >
                  <Text data-testid="chatTypographyElement">
                    {chatResponse.summary && (
                      <MarkdownComponent
                        data-testid="chat-answer-markdown"
                        compact
                      >
                        {chatResponse.summary}
                      </MarkdownComponent>
                    )}
                    {chatResponse?.data?.columns &&
                      chatResponse?.data?.rows &&
                      chatResponse?.data?.rows.length > 0 && (
                        <Box overflow="auto">
                          <DataTable
                            data={chatResponse?.data?.rows}
                            columns={chatResponse?.data?.columns?.map(
                              (column, index) => ({
                                property: column.name,
                                header: (
                                  <TextEmphasis>{column.name}</TextEmphasis>
                                ),
                                align:
                                  column.type === 'integer' ? 'end' : 'start',
                                render: datum => {
                                  /*
                                    The data can be multiple shapes:
                                    1. A nested array where each sub-array represents a 
                                       row with column values.
                                      Example: How many servers have auto ilo fw update 
                                               and how many don't have?
                                                  [true, 41]       

                                    2. An array of objects where name represents the resource 
                                       name(hostname/serial number), id represents the unique 
                                       identifier, and type can be any additional type info.
                                      Example: List all servers 
                                                  {
                                                    type: 'compute-ops/server',
                                                    id: '944140-I98+8899944140298039',
                                                    name: 'localhost'
                                                  }

                                    3. A combination of above cases.
                                      Example: Give me power state for all servers
                                                  [
                                                    { 
                                                      type: 'compute-ops/server', 
                                                      id: '168261-X99+8899168261599271', 
                                                      name: 'localhost.localdomain' 
                                                    }, 'ON'
                                                  ]
                                  */
                                  if (Array.isArray(datum)) {
                                    const item = datum[index];
                                    return handleRowData(item);
                                  }
                                  return handleRowData(datum);
                                },
                              }),
                            )}
                          />
                        </Box>
                      )}
                  </Text>

                  {chatResponse?.summary &&
                    chatResponse?.followupQuestions &&
                    showFollowUpQuestions && (
                      <Box
                        gap="small"
                        direction="column"
                        margin={{ top: 'medium' }}
                        data-testid="followup-questions-box"
                      >
                        {chatResponse.followupQuestions.length > 0 &&
                          chatResponse.questionType === 'FOLLOW_UP' && (
                            <Text margin={{ bottom: 'xsmall' }}>
                              {t('chat.chat_follow_up')}
                            </Text>
                          )}
                        {chatResponse.followupQuestions.map(
                          (question, index) => (
                            <ChatQuestionButton
                              key={index}
                              question={question}
                              onClick={() => {
                                hideAllFollowUpQuestions();
                                hideAllFeedback();
                                handleQueryClick && handleQueryClick(question);
                              }}
                            />
                          ),
                        )}
                      </Box>
                    )}

                  {chatResponse.summary && showFeedback && (
                    <Box
                      gap="xsmall"
                      direction="row"
                      margin={{ top: 'xsmall' }}
                    >
                      <ThumbsRating
                        onChange={click => {
                          if (click?.value === 'like') {
                            handleFeedbackClick(1);
                          } else if (click?.value === 'dislike') {
                            handleFeedbackClick(-1);
                          }
                        }}
                        disabled={feedbackLocked}
                        size="medium"
                      />
                      <Text
                        margin={{ left: 'small', top: 'xsmall' }}
                        size="small"
                      >
                        {getFeedbackMessage()}
                      </Text>
                    </Box>
                  )}

                  {showFeedbackPopup && showFeedback && (
                    <Box
                      ref={feedbackPopupRef}
                      background="background-front"
                      elevation="small"
                      round="small"
                      pad="medium"
                      margin={{ top: 'small' }}
                      width="medium"
                      data-testid="inline-feedback-card"
                    >
                      <Text margin="none" size="large" color="text-strong">
                        {t('chat.feedback.additional_feedback')}
                      </Text>
                      <Form
                        messages={{
                          required: t('chat.feedback.required'),
                        }}
                        onSubmit={submitFeedback}
                      >
                        <FormField required name="feedback">
                          <TextArea
                            placeholder={t('chat.feedback.placeholder')}
                            value={feedback}
                            onChange={event => {
                              setFeedback(event.target.value);
                            }}
                            onKeyDown={event => {
                              if (event.key === 'Enter' && !event.shiftKey) {
                                event.preventDefault();
                                if (event.target.form) {
                                  event.target.form.requestSubmit();
                                }
                              }
                            }}
                            name="feedback"
                          />
                        </FormField>
                        <Box
                          direction="row"
                          gap="medium"
                          justify="start"
                          margin={{ top: 'small' }}
                        >
                          <Button
                            label={t('chat.feedback.submit_btn')}
                            primary
                            type="submit"
                          />
                          <Button
                            label={
                              <Text weight="bold">
                                {t('chat.feedback.skip_btn')}
                              </Text>
                            }
                            onClick={() => {
                              setFeedbackLocked(true);
                              closePopup();
                            }}
                            plain
                          />
                        </Box>
                      </Form>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {showErrorModal && ( // Error modal
          <Layer onEsc={closeErrorModal}>
            <Box pad="medium" gap="small" width="medium">
              <TextEmphasis>{t('chat.feedback.error_message')}</TextEmphasis>
              <Text>{t('chat.feedback.try_again')}</Text>
              <Box direction="row" justify="end">
                <Button
                  primary
                  label={t('chat.feedback.close_btn')}
                  onClick={closeErrorModal}
                />
              </Box>
            </Box>
          </Layer>
        )}
      </Box>
    )
  );
};

export default ChatAnswer;

ChatAnswer.propTypes = {
  answer: PropTypes.any.isRequired,
};
